import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search as SearchIcon } from "lucide-react";
import { TID } from "@/constants/testIds";

const links = [
  { to: "/", label: "Home", tid: TID.navHome },
  { to: "/releases", label: "Music", tid: TID.navReleases },
  { to: "/lyrics", label: "Lyrics", tid: TID.navLyrics },
  { to: "/about", label: "About", tid: TID.navAbout },
  { to: "/press", label: "Press", tid: TID.navPress },
  { to: "/licensing", label: "Licensing", tid: TID.navLicensing },
  { to: "/contact", label: "Contact", tid: TID.navContact },
  { to: "/support", label: "Support", tid: TID.navSupport },
];

const Nav = ({ onOpenSearch }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <motion.nav
        data-testid={TID.nav}
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled ? "glass-strong border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 md:px-10 h-16 md:h-20">
          <Link to="/" className="group flex items-baseline gap-3">
            <span className="font-display text-xl md:text-2xl tracking-[0.4em] text-white group-hover:saffron-text-glow transition-all">
              KASHiX
            </span>
            <span className="hidden md:inline font-hindi text-xs text-[#FF9933]/80 tracking-widest">
              आस्था · अनंत
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.tid}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative text-[11px] uppercase tracking-[0.28em] transition-colors ${
                    isActive ? "text-[#FF9933]" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute -bottom-2 left-0 right-0 h-px bg-[#FF9933]"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onOpenSearch}
              data-testid="nav-search-button"
              aria-label="Open search"
              className="p-2 rounded-full text-white/80 hover:text-[#FF9933] hover:bg-white/5 transition"
            >
              <SearchIcon size={18} />
            </button>
            <button
              aria-label="Open menu"
              data-testid={TID.navMenuOpen}
              onClick={() => setOpen(true)}
              className="lg:hidden text-white p-2"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-2xl flex flex-col"
          >
            <div className="flex items-center justify-between h-16 px-6">
              <span className="font-display tracking-[0.4em] text-white">KASHiX</span>
              <button
                aria-label="Close menu"
                data-testid={TID.navMenuClose}
                onClick={() => setOpen(false)}
                className="text-white p-2"
              >
                <X size={22} />
              </button>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center gap-6">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`${l.tid}-mobile`}
                    end={l.to === "/"}
                    className={({ isActive }) =>
                      `font-display text-3xl tracking-widest ${isActive ? "text-[#FF9933]" : "text-white"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
